import React from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { BUN_CHART_FONT } from './chartTheme';
import Card from './Card';
import Badge from './Badge';
import ChartTooltip from './ChartTooltip';

const COLORS = ['var(--bun-data-violet)', 'var(--bun-data-sky)', 'var(--bun-data-amber)'];

/** Labeled comparison lines with caller-computed p10/p25/p50/p75/p90 bands. */
export default function PercentileChart({
  data = [],
  series = [],
  selectedIndex = 0,
  height = 360,
  valueFormatter = String,
  labelFormatter = String,
  xTickFormatter,
  yAxisFormatter,
  yDomain,
  rangeVariant = 'adjacent',
  referenceLines = [],
  tooltipRows,
  showLegend = true,
  rangeLabel = '10th percentile',
  tooltipNote,
  'aria-label': label = 'Comparison with simulated range',
}) {
  const rows = data.map((point) => {
    const row = { label: point.label, percentiles: point.percentiles };
    point.values.forEach((value, index) => {
      row[`series${index}`] = value;
    });
    if (point.percentiles) {
      row.base = point.percentiles[0];
      for (let i = 1; i < 5; i += 1)
        row[`band${i}`] = point.percentiles[i] - point.percentiles[i - 1];
      if (rangeVariant === 'nested') {
        row.outerRange = point.percentiles[4] - point.percentiles[0];
        row.innerBase = point.percentiles[1];
        row.innerRange = point.percentiles[3] - point.percentiles[1];
      }
    }
    return row;
  });
  const hasRange = rows.some((row) => row.percentiles);
  const rangeColor = COLORS[Math.max(selectedIndex, 0) % COLORS.length];
  if (!rows.length)
    return (
      <div
        style={{ height }}
        className="flex items-center justify-center text-bun-body text-bun-stone"
      >
        No data
      </div>
    );
  return (
    <div
      role="img"
      aria-label={label}
      className="min-w-0 w-full font-instrument text-bun-meta text-bun-stone"
    >
      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart data={rows} margin={{ top: 12, right: 8, bottom: 0, left: 0 }}>
          <CartesianGrid stroke="var(--bun-line-soft)" vertical={false} />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            minTickGap={24}
            tickFormatter={xTickFormatter}
            tick={{ fill: 'var(--bun-stone)', fontFamily: BUN_CHART_FONT, fontSize: 11 }}
          />
          <YAxis
            width={48}
            axisLine={false}
            tickLine={false}
            tickFormatter={yAxisFormatter}
            domain={yDomain}
            tick={{ fill: 'var(--bun-stone)', fontFamily: BUN_CHART_FONT, fontSize: 11 }}
          />
          <Tooltip
            content={({ active, payload }) => {
              const point = payload?.[0]?.payload;
              if (!active || !point) return null;
              if (tooltipRows)
                return (
                  <ChartTooltip
                    title={labelFormatter(point.label)}
                    rows={tooltipRows(point.label)}
                    valueFormatter={valueFormatter}
                  />
                );
              return (
                <Card raised>
                  <Card.Section className="space-y-2">
                    <p className="font-semibold">{labelFormatter(point.label)}</p>
                    {series.map(
                      (name, index) =>
                        point[`series${index}`] != null && (
                          <p key={index} className="flex justify-between gap-4">
                            <Badge variant="dot" color={COLORS[index % COLORS.length]}>
                              {name}
                            </Badge>
                            <span>{valueFormatter(point[`series${index}`])}</span>
                          </p>
                        )
                    )}
                    {point.percentiles && (
                      <div className="border-t border-bun-line-soft pt-2">
                        {tooltipNote && <p>{tooltipNote}</p>}
                        <p>
                          {rangeLabel}: {valueFormatter(point.percentiles[0])}
                        </p>
                      </div>
                    )}
                  </Card.Section>
                </Card>
              );
            }}
          />
          {hasRange && rangeVariant === 'nested' && (
            <>
              <Area
                type="monotone"
                dataKey="base"
                stackId="outer"
                fill="transparent"
                stroke="none"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="outerRange"
                stackId="outer"
                fill={rangeColor}
                fillOpacity={0.1}
                stroke="none"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="innerBase"
                stackId="inner"
                fill="transparent"
                stroke="none"
                isAnimationActive={false}
              />
              <Area
                type="monotone"
                dataKey="innerRange"
                stackId="inner"
                fill={rangeColor}
                fillOpacity={0.18}
                stroke="none"
                isAnimationActive={false}
              />
            </>
          )}
          {hasRange && rangeVariant !== 'nested' && (
            <>
              <Area
                type="monotone"
                dataKey="base"
                stackId="range"
                fill="transparent"
                stroke="none"
                isAnimationActive={false}
              />
              {[1, 2, 3, 4].map((index) => (
                <Area
                  key={index}
                  type="monotone"
                  dataKey={`band${index}`}
                  stackId="range"
                  fill={rangeColor}
                  fillOpacity={index === 1 || index === 4 ? 0.07 : 0.14}
                  stroke="none"
                  isAnimationActive={false}
                />
              ))}
            </>
          )}
          {series.map((name, index) => (
            <Line
              key={index}
              name={name}
              type="monotone"
              dataKey={`series${index}`}
              stroke={COLORS[index % COLORS.length]}
              strokeWidth={index === selectedIndex ? 3 : 1.5}
              opacity={index === selectedIndex ? 1 : 0.4}
              strokeDasharray={index >= COLORS.length ? '4 3' : undefined}
              dot={false}
              isAnimationActive={false}
            />
          ))}
          {referenceLines.map((reference, index) => (
            <ReferenceLine
              key={index}
              x={reference.x}
              y={reference.y}
              stroke={reference.color || 'var(--bun-stone)'}
              strokeDasharray="4 4"
              label={
                reference.label
                  ? {
                      value: reference.label,
                      fill: reference.color || 'var(--bun-stone)',
                      fontFamily: BUN_CHART_FONT,
                      fontSize: 11,
                      position: reference.position || 'insideTopRight',
                    }
                  : undefined
              }
            />
          ))}
        </ComposedChart>
      </ResponsiveContainer>
      {showLegend && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2">
          {series.map((name, index) => (
            <Badge
              key={index}
              variant="dot"
              color={COLORS[index % COLORS.length]}
              className={index === selectedIndex ? 'font-semibold text-bun-text' : ''}
            >
              {name}
            </Badge>
          ))}
          {hasRange && (
            <Badge variant="dot" color={rangeColor}>
              Simulated range
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
